/**
 * Stable SDD feature gate keys — must match migration seeds in
 * `packages/database/drizzle/migrations/0122_sdd_feature_gates.sql`,
 * `0123_member_integration_connections.sql` (additional catalog rows), and
 * `0161_tech_plan_agent_profile_dc370.sql` (Tech Plan generation gate),
 * `0167_task_generation_profile_dc371.sql` (Task generation gate), and
 * `0169_test_plan_scenario_profile_dc372.sql` (Test scenario generation gate),
 * `0184_test_scenario_generation_real_model.sql` (Test scenario real model gate), and
 * `0170_sdd_implementation_readiness_dc373.sql` (implementation readiness rollup), and
 * `0171_production_traceability_graph_dc374.sql` (production traceability graph), and
 * `0173_governance_standards_workflows_dc388.sql` (governance standards workflows + routing), and
 * `0178_mcp_compliance_analysis_attempt_dc382.sql` (MCP server production compliance pilot gate), and
 * `0179_hosted_mcp_proposal_tool_gating_dc380.sql` (Hosted MCP proposal-class rollout dimensions), and
 * `0198_artifact_generation_review.sql` (artifact generation review pass gate), and
 * `0199_opencode_harness_dc413.sql` (OpenCode external harness pilot gates), and
 * `0227_artifact_generation_implementability_lints.sql` (persist remainder lints), and
 * `0235_repository_branch_change.sql` (post-confirm branch change) and
 * `0238_repository_branch_change_default_on.sql` (catalog default true), and
 * `0240_requirement_decisions.sql` (requirement claims, search, and proposals; default off), and
 * `0244_requirement_conflict_writes.sql` (board writes on Accept; default off), and
 * `0246_artifacts_redesign_gate.sql` (redesigned Artifacts page; default off).
 */
/** Logical MCP server / worker bundle keys (DC-382); physical merge uses catalog rows above. */
export const MCP_SERVER_FEATURE_GATE_KEYS = {
    /** DC-382 — pilot expensive production branch for `work_item_compliance_evaluate`. */
    CHECK_COMPLIANCE_ENABLED: 'mcp_server.check_compliance_enabled',
};
export const SDD_FEATURE_GATE_KEYS = {
    MCP_TOOL_SURFACE: 'sdd_mcp_tool_surface',
    /** Production-hosted MCP Streamable HTTP surface (DC-425). */
    HOSTED_MCP_ENABLED: 'hosted_mcp.enabled',
    /** DC-380 — `work_item_clarification_request` org rollout dimension. */
    HOSTED_MCP_PROPOSAL_CLARIFICATION_ENABLED: 'hosted_mcp.proposal_clarification.enabled',
    /** DC-380 — `work_item_specification_propose` / `work_item_technical_plan_propose`. */
    HOSTED_MCP_PROPOSAL_ARTIFACT_REVISION_ENABLED: 'hosted_mcp.proposal_artifact_revision.enabled',
    /** DC-380 — `work_item_compliance_evaluate`. */
    HOSTED_MCP_PROPOSAL_COMPLIANCE_ADVISORY_ENABLED: 'hosted_mcp.proposal_compliance_advisory.enabled',
    /** When enabled, demo MCP read tools require a connected per-member integration (DC-376). */
    MCP_MEMBER_INTEGRATION_REQUIRED: 'sdd_mcp_member_integration_required',
    SPEC_GENERATION_WORKER: 'sdd_spec_generation_worker',
    /** DC-449 / STORY-0116 — managed Anthropic spec draft path (migration 0160). */
    SPEC_GENERATION_REAL_MODEL: 'sdd_spec_generation_real_model',
    /** Program prerequisite for real spec-generation model path (DC-449 FR-019). */
    ARTIFACT_AGENT: 'sdd_artifact_agent',
    /** DC-370 — Tech Plan agent profile org rollout (maps from Jira `tech_plan_generation.enabled`). */
    TECH_PLAN_GENERATION_ENABLED: 'tech_plan_generation.enabled',
    /** DC-371 — Task generation agent profile (maps from Jira `task_generation.enabled`). */
    TASK_GENERATION_ENABLED: 'task_generation.enabled',
    /** 410 — managed model path for tech plan generation (migration 0183). */
    TECH_PLAN_GENERATION_REAL_MODEL: 'tech_plan_generation_real_model',
    /** 410 — managed model path for task generation (migration 0183). */
    TASK_GENERATION_REAL_MODEL: 'task_generation_real_model',
    /** DC-372 — Test plan / scenario agent profile org rollout (`test_scenario_generation.enabled`). */
    TEST_SCENARIO_GENERATION_ENABLED: 'test_scenario_generation.enabled',
    /** 412 — managed model path for test scenario generation (migration 0184). */
    TEST_SCENARIO_GENERATION_REAL_MODEL: 'test_scenario_generation_real_model',
    /** DC-373 / STORY-0123 — implementation readiness (Implement, snippets, Jira thread). */
    IMPLEMENTATION_READINESS_ENABLED: 'implementation_readiness.enabled',
    /** DC-374 / STORY-0124 — production traceability graph (member bundle + edges). */
    PRODUCTION_TRACEABILITY_GRAPH_ENABLED: 'production_traceability_graph.enabled',
    /** DC-388 / STORY-0151 — parent gate for Govern standards catalog, applicability, lifecycle. */
    GOVERNANCE_STANDARDS_WORKFLOWS_ENABLED: 'governance_standards_workflows.enabled',
    /** DC-388 / STORY-0151 — child gate for routing mutations + governance_routing inbox. */
    GOVERNANCE_STANDARDS_ROUTING_ENABLED: 'governance_standards_routing.enabled',
    /** @deprecated Not evaluated — memory follows GOVERNANCE_STANDARDS_WORKFLOWS_ENABLED */
    ENGINEERING_MEMORY_ENABLED: 'engineering_memory.enabled',
    /** DC-421 / STORY-0213 — supersession + stale-work orchestration (seeded in migration 0158). */
    SUPERSESSION_STALE_WORK: 'sdd_supersession_stale_work',
    /** DC-366 / STORY-0113 — clarification request loop for spec generation (migration 0161). */
    SPEC_CLARIFICATION_REQUEST_LOOP: 'sdd_spec_clarification_request_loop',
    /** DC-448 / STORY-0228 — lightweight artifact comment routing classifier rollout (migration 0166). */
    ARTIFACT_COMMENT_ROUTING_CLASSIFIER: 'sdd_artifact_comment_routing_classifier',
    NEXT_SHELL_ROLLOUT: 'sdd_next_shell_rollout',
    /** Second-pass artifact review after managed generation (claim-evidence grounding). */
    ARTIFACT_GENERATION_REVIEW_ENABLED: 'artifact_generation_review.enabled',
    /** Persist remainder lints for Tech Plan / Tasks / Tests (Story 1, migration 0227, default on). */
    ARTIFACT_GENERATION_IMPLEMENTABILITY_LINTS_ENABLED: 'artifact_generation_implementability_lints.enabled',
    /** Feature 413 — OpenCode external harness for tech plan generation (migration 0199, default off). */
    TECH_PLAN_GENERATION_OPENCODE_HARNESS: 'tech_plan_generation_opencode_harness.enabled',
    /** DC-451 — durable per-provider SDD generation gate policy + settings UI. */
    SDD_GENERATION_GATE_POLICY_ENABLED: 'sdd_generation_gate_policy.enabled',
    /** Jira OAuth webhook production SDD intake (default off). */
    JIRA_OAUTH_SDD_WEBHOOK_INTAKE_ENABLED: 'jira_oauth_sdd_webhook_intake.enabled',
    /** Jira dynamic webhook auto-registration after OAuth connect (default off). */
    JIRA_DYNAMIC_WEBHOOK_REGISTRATION_ENABLED: 'jira_dynamic_webhook_registration.enabled',
    /**
     * Post-confirm Git branch change for SDD artifacts (migration 0235; catalog default true in 0238).
     * Included in new-org full-experience bootstrap.
     */
    REPOSITORY_BRANCH_CHANGE_ENABLED: 'repository_branch_change.enabled',
    /**
     * Requirement claims, board search, and overlap proposals (migration 0240, default off).
     * Excluded from new-organization bootstrap so a new org does not spend tokens on it.
     */
    REQUIREMENT_DECISIONS_ENABLED: 'requirement_decisions.enabled',
    /**
     * On Accept, re-read the tickets and write the drafted title, description, and close
     * (migration 0244, default off). Excluded from new-organization bootstrap.
     * Off leaves Accept as a status mark and does not call the board.
     */
    REQUIREMENT_CONFLICT_WRITES_ENABLED: 'requirement_conflict_writes.enabled',
    /**
     * Redesigned Artifacts page on the work item detail page (migration 0246, default off).
     * Excluded from new-organization bootstrap. Frontend only: off shows the current page.
     */
    ARTIFACTS_REDESIGN_ENABLED: 'artifacts_redesign.enabled',
};
/** DC-419 — must match `0131_repository_discovery_handoff_dc419.sql` catalog seeds */
export const REPOSITORY_DISCOVERY_GATE_KEYS = {
    ENABLED: 'repository_discovery.enabled',
    REQUIRE_CONFIRMATION: 'repository_discovery.require_confirmation',
};
//# sourceMappingURL=gate-keys.js.map